import { ConversationType, GroupTipOperationType, MessagePriorityType, MessageType } from './enum';

type MessagePayload = TextPayload | CustomPayload | GroupTipPayload | GroupSystemNoticePayload;

// export interface Message<T extends MessagePayload = MessagePayload> {
export interface Message {
  ID: string;
  type: MessageType;
  payload: any;
  conversationID: string;
  conversationType: ConversationType;
  // 接收方的 userID
  to: string;

  // 发送方的 userID，在消息发送时，会默认设置为当前登录的用户
  from: string;

  // 消息的流向
  // in 为收到的消息
  // out 为发出的消息
  flow: 'in' | 'out';
  // 消息时间戳。单位：秒
  time: number;

  // 消息状态。
  // fail(发送失败)
  // unSend(未发送)
  // success(发送成功)
  status: 'unSend' | 'success' | 'fail';
  // 是否被撤回的消息，true 标识被撤回的消息（v2.4.0起支持）
  isRevoked: boolean;

  // 消息优先级，用于群聊（v2.4.2起支持）
  priority: MessagePriorityType;

  // 消息发送者的昵称（v2.6.0起，在 AVChatRoom 内支持，需提前调用 updateMyProfile 设置）
  nick: string;

  // 消息发送者的头像地址（v2.6.0起，在 AVChatRoom 内支持，需提前调用 updateMyProfile 设置）
  avatar: string;

  // C2C 消息对端是否已读，true 标识对端已读（v2.7.0起支持）
  isPeerRead: boolean;

  // 消息发送者的群名片（v2.9.0起支持，也可称之为消息发送者的群昵称），需提前调用 setGroupMemberNameCard 设置
  nameCard: string;

  // 群聊时此字段存储被 at 的群成员的 userID（v2.9.0起支持）
  atUserList: string[];

  // 消息自定义数据（云端保存，会发送到对端，程序卸载重装后还能拉取到，v2.10.2起支持）
  cloudCustomData: string;

  // 是否被删除的消息，true 标识被删除的消息（v2.12.0起支持）
  isDeleted: boolean;

  // 是否被第三方回调修改过，true 标识被第三方回调修改过的消息（v2.12.1起支持）
  isModified: boolean;
}

export interface TextPayload {
  text: string;
}

export enum GroupJoinType {
  DEFAULT,
  APPLY,
  INVITE,
}

export interface GroupTipPayload {
  // 加群类型，具体如下：
  //
  // 值	含义
  // 0	默认值，表示该行为不是加群操作
  // 1	申请加群
  // 2	邀请加群
  groupJoinType: number;
  // 执行该操作的用户 ID
  operatorID: string;

  // 操作类型，具体如下:
  //
  //   操作类型	值	含义
  // TIM.TYPES.GRP_TIP_MBR_JOIN	1	有成员加群
  // TIM.TYPES.GRP_TIP_MBR_QUIT	2	有群成员退群
  // TIM.TYPES.GRP_TIP_MBR_KICKED_OUT	3	有群成员被踢出群
  // TIM.TYPES.GRP_TIP_MBR_SET_ADMIN	4	有群成员被设为管理员
  // TIM.TYPES.GRP_TIP_MBR_CANCELED_ADMIN	5	有群成员被撤销管理员
  // TIM.TYPES.GRP_TIP_GRP_PROFILE_UPDATED	6	群组资料变更
  // TIM.TYPES.GRP_TIP_MBR_PROFILE_UPDATED	7	群成员资料变更，例如：群成员被禁言
  operationType: GroupTipOperationType;

  // 相关的 userID 列表
  userIDList: string[];

  // 若是群资料变更，该字段存放变更的群资料
  //
  // Properties
  // Name	Type	Description
  // groupCustomField	Array.<Object>
  // 若是群自定义字段变更，该字段存放变更的群自定义字段键值对（v2.6.0起支持）
  newGroupProfile: { groupCustomField: any[] };

  // 当群成员被禁言时，可在该字段中拿到相关信息
  //
  // Properties
  // Name	Type	Description
  // userID	String
  // muteTime	Number
  // 被禁言的时长，单位：秒
  memberList: {
    userID: string;
    muteTime: number;
  }[];
}

export interface GroupSystemNoticePayload {
  // 执行该操作的用户 ID
  operatorID: string;

  // 操作类型，具体如下：
  //
  // 值	描述	接收对象
  // 1	有用户申请加群	群管理员/群主接收
  // 2	申请加群被同意	申请加群的用户接收
  // 3	申请加群被拒绝	申请加群的用户接收
  // 4	被踢出群组	被踢出的用户接收
  // 5	群组被解散	全体群成员接收
  // 6	创建群组	创建者接收
  // 7	邀请加群	被邀请者接收
  // 8	退群	退群者接收
  // 9	设置管理员	被设置方接收
  // 10	取消管理员	被取消方接收
  // 11	群已被回收	全员接收，不展示
  // 12	收到加群邀请，被邀请者需要同意或者拒绝	被邀请者接收
  // 13	邀请他人加群，被他人同意	邀请发起者接收
  // 14	邀请他人加群，被他人拒绝	邀请发起者接收
  // 15	已读上报多终端同步通知	只有上报人自己收到
  // 255	用户自定义通知	默认全员接收
  operationType: number;
  // 相关的群组资料
  groupProfile: any;

  // 用户自定义字段。使用 RestAPI 发送自定义通知时，可在该属性值中拿到自定义通知的内容。
  userDefinedField: string;

  // 处理的附言。例如：user1 申请加入 group1 时，若进群需要验证，且 user1 填写了申请加群的附言。则 group1 的管理员会在相应群系统通知中看到该字段。
  handleMessage: any;
}

/**
 * 所有的互动类型均为 InviteCustomPayloadExtension
 * 所有的白板类型均为 WhiteBoardCustomPayloadExtension
 * 修改音视频状态的为 ChangeStreamStatusExtension
 * 修改群成员资料的为 ChangeGroupMemberProfile
 * 老师发布了互动消息，如签到之类的 PublishTaskProfile
 */
export enum CustomPayloadExtensionType {
  InviteCustomPayloadExtension = 'InviteCustomPayloadExtension',
  KickCustomPayloadExtension = 'KickCustomPayloadExtension',
  WhiteBoardCustomPayloadExtension = 'TXWhiteBoardExt',
  ChangeStreamStatusExtension = 'ChangeStreamStatus',
  ChangeGroupMemberProfile = 'ChangeGroupMemberProfile',
  GroupMemberListIncrease = 'GroupMemberListIncrease',
  PublishTaskProfile = 'PublishTaskProfile',
}

export interface CustomPayload {
  /**
   * @type {CustomPayloadData}
   */
  data: string;
  description?: string;
  /**
   * @type {CustomPayloadExtensionType}
   * 类型标识
   */
  extension: CustomPayloadExtensionType;
}

export interface CreateMessageInput<T extends MessagePayload> {
  // 消息接收方的 userID 或 groupID
  to: string;

  // 会话类型，取值TIM.TYPES.CONV_C2C（端到端会话） 或 TIM.TYPES.CONV_GROUP（群组会话）
  conversationType: ConversationType;

  // 消息优先级
  // TIM.TYPES.MSG_PRIORITY_NORMAL
  priority?: MessagePriorityType;

  // 消息内容的容器
  payload: T;
}

export interface CustomPayloadData {
  // 消息接收方的userId
  at?: string;
}

export interface CustomChangeStreamPayloadData extends CustomPayloadData {
  type: 'video' | 'audio';
  enable: boolean;
}

export interface CustomChangeProfilePayloadData extends CustomPayloadData {
  admin: boolean;
}

export interface MessageReceivedEventData {
  name: string;
  data: Message[];
}
